import { Link, useLocation } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const hideLoginBtn = urlParams.get('hideLoginBtn') === 'true';

  return (
    <div className="min-h-screen bg-[#006B82] py-8 px-4 sm:px-6">
      <div className="light max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <div className="mb-8 border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            This Privacy Policy describes how Gio collects, uses, and protects
            your information.
          </p>
        </div>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              About Gio
            </h2>
            <p>
              Gio (&quot;the Platform&quot;) is a wholly owned subsidiary and
              product of BK★CODE LLC, a New York limited liability company.
            </p>
            <p className="mt-2">
              BK★CODE LLC owns and operates Gio and all associated intellectual
              property, trademarks, software, services, and related assets.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              1. Information We Collect
            </h2>
            <p className="mb-2">We may collect the following information:</p>
            <div className="mb-2">
              <p className="font-medium">Account Information</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name</li>
                <li>Business name</li>
                <li>Contact information</li>
              </ul>
            </div>
            <div className="mb-2">
              <p className="font-medium">Operational Data</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Orders and item details</li>
                <li>Notes, images, and instructions</li>
                <li>Delivery and fulfillment activity</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">Technical Data</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Device and browser information</li>
                <li>IP address</li>
                <li>Usage logs</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              2. How We Use Information
            </h2>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Operate and maintain the platform</li>
              <li>Process and manage orders</li>
              <li>Facilitate workflows between users</li>
              <li>Provide support and communication</li>
              <li>Improve functionality and performance</li>
              <li>Maintain security and prevent misuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              3. Data Sharing
            </h2>
            <p className="mb-2">Gio does not sell user data.</p>
            <p className="mb-2">
              Gio does not share user data with third parties for marketing
              purposes.
            </p>
            <p className="mb-2">We may share data only when necessary to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Operate the service</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="mt-2">
              If payment processing is introduced, limited necessary information
              may be shared with third-party providers solely to facilitate
              transactions. These providers operate under their own privacy
              policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              4. Data Security
            </h2>
            <p className="mb-2">
              We implement reasonable safeguards to protect your data.
            </p>
            <p>
              However, no system is completely secure, and we cannot guarantee
              absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              5. Data Retention & Deletion
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Users may request deletion of their data</li>
              <li>
                Data will be deleted or anonymized where reasonably possible
              </li>
              <li>
                Some data may be retained for legal or operational purposes
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              6. Platform Reliability Disclaimer
            </h2>
            <p className="mb-2">Gio is not responsible for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Data loss</li>
              <li>Service interruptions</li>
              <li>Technical outages</li>
            </ul>
            <p className="mt-2">
              Users are encouraged to maintain their own records where
              appropriate.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              7. Your Rights
            </h2>
            <p className="mb-2">You may:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Request access to your data</li>
              <li>Request corrections</li>
              <li>Request deletion</li>
            </ul>
            <p className="mt-2">
              Contact:{' '}
              <a
                href="mailto:app@giotheapp.com"
                className="text-[#006B82] hover:underline"
              >
                app@giotheapp.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              8. Changes to Policy
            </h2>
            <p>
              Gio may update this Privacy Policy at any time. Continued use of
              the platform constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              9. Contact
            </h2>
            <p>
              For privacy-related inquiries:{' '}
              <a
                href="mailto:app@giotheapp.com"
                className="text-[#006B82] hover:underline"
              >
                app@giotheapp.com
              </a>
            </p>
          </section>
        </div>

        {!hideLoginBtn && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link
              to="/login"
              className="inline-flex items-center text-[#006B82] hover:text-[#00809C] transition-colors font-medium"
            >
              ← Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
